<script lang="ts" setup>
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer, LGeoJson } from '@maxel01/vue-leaflet'
import L, { Map, Marker, DivIcon, LatLng } from 'leaflet'
import { ref, computed, nextTick, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { storeToRefs } from 'pinia'
import { useApi } from '../composables/api'
import { useDiscoveryStore } from '../stores/discovery/discovery.store'
import IncidentMapPopup from './IncidentMapPopup.vue'
import { regionConfig } from '@/config/region'
import { reverseGeocodeToDisplayName } from '../composables/useNominatim'

const maxBounds = new L.LatLngBounds(
  [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
  [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
)

const { discoveryApi, previewApi } = useApi()
const $q = useQuasar()
const discoveryStore = useDiscoveryStore()
const { zoom, center, selectedFeature, popupIncident, clusterIncidents, clusterNearbyAddress } =
  storeToRefs(discoveryStore)

const mapInstance = ref<Map | null>(null)
const geoJsonData = ref<any>(null)
const isLoading = ref<boolean>(true)
const isPopupLoading = ref(false)
const popupAnchor = ref<{ x: number; y: number } | null>(null)

const fetchController = ref<AbortController | null>(null)
const searchOptions = ref<any[]>([])
const selectedSearchResult = ref<any>(null)
const isSearchOpen = ref(false)
const mapContainerVisible = ref(false)

onMounted(() => {
  mapContainerVisible.value = true
})

const POPUP_WIDTH = 320
const LOCATION_CONSENT_KEY = 'discovery:location-consent'

const popupStyle = computed(() => {
  if (!popupAnchor.value || $q.screen.lt.sm) return {}

  const container = mapInstance.value?.getContainer()
  const containerWidth = container?.offsetWidth ?? 800
  const containerHeight = container?.offsetHeight ?? 600
  const { x, y } = popupAnchor.value
  const margin = 16

  // Horizontal: right of marker, flip left if it would overflow
  let left = x + margin
  if (left + POPUP_WIDTH > containerWidth - margin) {
    left = x - POPUP_WIDTH - margin
  }
  left = Math.max(margin, left)

  // Vertical: position with 30% above the marker, clamped so popup stays inside the container.
  // Estimate the popup height as CSS max-height (50vh) since actual height isn't known yet.
  const estimatedHeight = Math.min(containerHeight * 0.5, 400)
  let top = y - estimatedHeight * 0.3
  top = Math.min(top, containerHeight - estimatedHeight - margin)
  top = Math.max(margin, top)

  return {
    left: `${left}px`,
    top: `${top}px`,
    bottom: 'auto',
    width: `${POPUP_WIDTH}px`
  }
})

const onMapReady = (map: Map) => {
  mapInstance.value = map
  fetchIncidents()

  if (window.history.state?.forward?.startsWith('/preview/')) {
    // Returning from a preview page — restore popup pixel position from stored feature
    if (selectedFeature.value) {
      const [lng, lat] = selectedFeature.value.geometry.coordinates
      const point = map.latLngToContainerPoint(new LatLng(lat, lng))
      popupAnchor.value = { x: point.x, y: point.y }
    }
  } else {
    discoveryStore.closePopup()
  }
}

const fetchIncidents = async () => {
  if (!mapInstance.value) return

  fetchController.value?.abort()
  const controller = new AbortController()
  fetchController.value = controller

  try {
    const bounds = mapInstance.value.getBounds()
    const response = await discoveryApi.discoveryGetIncidentsByBboxAndZoomAndStartDateAndEndDate(
      {
        bbox: bounds.toBBoxString(),
        zoom: Math.round(mapInstance.value.getZoom())
      },
      { signal: controller.signal }
    )
    geoJsonData.value = response ? { ...response, features: response.features ?? [] } : null
  } catch (error) {
    // Error.cause is ES2022 but the base tsconfig lib predates it, so type it locally.
    const cause = error instanceof Error ? (error as Error & { cause?: unknown }).cause : null
    if (
      (error instanceof DOMException && error.name === 'AbortError') ||
      (cause instanceof DOMException && cause.name === 'AbortError')
    )
      return
    console.error('Failed to fetch map features:', error)
  } finally {
    isLoading.value = false
  }
}

const onMarkerClick = async (feature: any, e: any) => {
  selectedFeature.value = feature
  popupIncident.value = null
  clusterIncidents.value = []
  clusterNearbyAddress.value = null
  isPopupLoading.value = true

  if (mapInstance.value) {
    const point = mapInstance.value.latLngToContainerPoint(e.latlng)
    popupAnchor.value = { x: point.x, y: point.y }
  }

  try {
    if (feature.properties.cluster) {
      const [lng, lat] = feature.geometry.coordinates
      clusterIncidents.value = await discoveryApi.discoveryGetClusterPreviewByLatAndLngAndZoom({
        lat,
        lng,
        zoom: zoom.value
      })
      fetchClusterAddress(lat, lng)
    } else {
      popupIncident.value = await previewApi.previewPreviewIncidentReportById({
        id: feature.properties.id
      })
    }
  } catch (error) {
    console.error('Failed to fetch popup data:', error)
  } finally {
    isPopupLoading.value = false
  }
}

const fetchClusterAddress = async (lat: number, lng: number) => {
  try {
    clusterNearbyAddress.value = await reverseGeocodeToDisplayName(lat, lng)
  } catch {
    // address is a best-effort enhancement, silence failures
  }
}

const closePopup = () => {
  discoveryStore.closePopup()
  popupAnchor.value = null
}

const onSearchFilter = async (val: string, doneFn: (cb: () => void) => void) => {
  if (!val || val.length < 2) {
    doneFn(() => {
      searchOptions.value = []
    })
    return
  }
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(val)}`
    )
    const data = await res.json()
    doneFn(() => {
      searchOptions.value = data
    })
  } catch {
    doneFn(() => {
      searchOptions.value = []
    })
  }
}

const onSearchSelect = (result: any) => {
  if (!result || !mapInstance.value) return
  const [south, north, west, east] = result.boundingbox.map(Number)
  mapInstance.value.fitBounds([
    [south, west],
    [north, east]
  ])
  nextTick(() => {
    selectedSearchResult.value = null
  })
}

const centreOnLocation = () => {
  const consent = localStorage.getItem(LOCATION_CONSENT_KEY)
  if (consent === 'granted') {
    requestLocation()
  } else if (consent !== 'denied') {
    $q.dialog({
      title: 'Use your location',
      message: 'Would you like to centre the map on your current location?',
      cancel: { label: 'No thanks', flat: true },
      ok: { label: 'Yes', color: 'primary' },
      persistent: true
    })
      .onOk(() => {
        localStorage.setItem(LOCATION_CONSENT_KEY, 'granted')
        requestLocation()
      })
      .onCancel(() => {
        localStorage.setItem(LOCATION_CONSENT_KEY, 'denied')
      })
  }
}

const requestLocation = () => {
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      mapInstance.value?.flyTo([pos.coords.latitude, pos.coords.longitude], 14)
    },
    () => {
      $q.notify({ type: 'negative', message: 'Unable to get your location.' })
    }
  )
}

const geoJsonOptions = {
  pointToLayer: (feature: any, latlng: LatLng) => {
    const props = feature.properties

    if (props.cluster) {
      return new Marker(latlng, {
        icon: new DivIcon({
          html: `<div style="display: flex; align-items: center; justify-content: center; width: 40px; height: 20px; ">
                  <i class="q-icon mdi mdi-map-marker-multiple" style="font-size: 20px; color: #e74c3c;" aria-hidden="true" role="presentation"></i>
                  <span style="background-color: #2c3e50; color: white; border-radius: 50%; width: 20px; height: 20px; font-weight: bold; text-align: center;">${props.pointCount}</span>
                 </div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
          className: ''
        })
      })
    }

    return new Marker(latlng, {
      icon: new DivIcon({
        html: `
          <i class="q-icon mdi mdi-map-marker" style="font-size: 20px; color: #e74c3c;" aria-hidden="true" role="presentation"></i>
        `,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        className: ''
      })
    })
  },

  onEachFeature: (feature: any, layer: any) => {
    layer.on('click', (e: any) => {
      L.DomEvent.stopPropagation(e)
      onMarkerClick(feature, e)
    })
  }
}
</script>

<template>
  <div style="position: relative; min-height: inherit; width: 100%">
    <q-inner-loading
      :showing="isLoading"
      label="Please wait..."
      label-class="text-teal"
      label-style="font-size: 1.1em"
    />

    <div class="map-search-overlay">
      <q-select
        v-model="selectedSearchResult"
        :options="searchOptions"
        option-label="display_name"
        use-input
        hide-selected
        fill-input
        input-debounce="500"
        placeholder="Search location..."
        dense
        outlined
        bg-color="white"
        class="search-select"
        behavior="menu"
        popup-content-class="map-search-dropdown"
        :popup-content-style="{
          maxHeight: $q.screen.lt.md ? '45dvh' : '320px',
          overflowY: 'auto'
        }"
        :class="{ 'search-open': isSearchOpen }"
        @popup-show="isSearchOpen = true"
        @popup-hide="isSearchOpen = false"
        @filter="onSearchFilter"
        @update:model-value="onSearchSelect"
      >
        <template #prepend>
          <q-icon name="mdi-magnify" size="xs" color="grey-6" />
        </template>
        <template #no-option>
          <q-item>
            <q-item-section class="text-grey">No results</q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>

    <div class="map-centre-btn">
      <q-btn
        round
        size="sm"
        color="white"
        text-color="grey-8"
        icon="mdi-crosshairs-gps"
        @click.stop="centreOnLocation"
      >
        <q-tooltip>Centre on my location</q-tooltip>
      </q-btn>
    </div>

    <l-map
      v-if="mapContainerVisible"
      v-model:zoom="zoom"
      v-model:center="center"
      :max-bounds="maxBounds"
      @ready="onMapReady"
      @moveend="fetchIncidents"
      @click="closePopup"
      style="min-height: inherit; width: 100%; height: 100%"
    >
      <l-tile-layer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        layer-type="base"
        name="OpenStreetMap"
      ></l-tile-layer>

      <l-geo-json v-if="geoJsonData" :geojson="geoJsonData" :options="geoJsonOptions" />
    </l-map>

    <Transition name="popup">
      <IncidentMapPopup
        v-if="selectedFeature"
        :is-cluster="selectedFeature.properties.cluster"
        :incident="popupIncident"
        :cluster-incidents="clusterIncidents"
        :is-loading="isPopupLoading"
        :total-count="selectedFeature.properties.pointCount ?? null"
        :nearby-address="clusterNearbyAddress"
        class="map-popup-overlay"
        :style="popupStyle"
        @close="closePopup"
      />
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.map-search-overlay {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  width: 300px;

  @media (max-width: $breakpoint-sm-max) {
    left: 3.75rem;
    width: calc(100% - 8.25rem);
    transform: none;
  }
}

.search-select {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  :deep(.q-field__control) {
    transition:
      border-bottom-left-radius 0.15s ease,
      border-bottom-right-radius 0.15s ease;
  }

  &.search-open :deep(.q-field__control) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.map-centre-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 50%;
}

.map-popup-overlay {
  position: absolute;
  z-index: 1000;
  bottom: 1.5rem;
  left: 1rem;
  width: 320px;
  max-height: 50vh;
  overflow-y: auto;

  @media (max-width: $breakpoint-sm-max) {
    left: 0.5rem;
    right: 0.5rem;
    width: auto;
    max-height: 60vh;
  }
}

.popup-enter-active,
.popup-leave-active {
  transition: all 0.2s ease;
}

.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>

<style lang="scss">
.map-search-dropdown {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
